import React from "react";
import { Button } from "@shared/ui/button";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";

interface AssetFilters {
  page: number;
  limit: number;
  assetType?: string;
  location?: string;
  conditionScore?: string;
  riskLevel?: string;
  search_data?: string;
}

interface AssetFilterPanelProps {
  currentFilters: Partial<AssetFilters>;
  onApplyFilters: (filters: Partial<AssetFilters>) => void;
  onResetFilters: () => void;
}

export const AssetFilterPanel: React.FC<AssetFilterPanelProps> = ({
  currentFilters,
  onApplyFilters,
  onResetFilters,
}) => {
  // Define form fields configuration
  const filterFields: FormField[] = [
    {
      name: "assetType",
      type: "select",
      label: "Asset Type",
      placeholder: "Select asset type",
      defaultValue: currentFilters.assetType || "all",
      options: [
        { value: "all", label: "All Types" },
        { value: "pump", label: "Pump" },
        { value: "transformer", label: "Transformer" },
        { value: "valve", label: "Valve" },
        { value: "motor", label: "Motor" },
      ],
      fullWidth: true,
    },
  ];

  // Create form instance with default values from current filters
  const form = FormService.createForm({
    fields: filterFields,
    defaultValues: {
      assetType: currentFilters.assetType || "all",
    },
  });

  // Handle form submission
  const handleApplyFilters = (data: any) => {
    const filtersToApply: Partial<AssetFilters> = {};

    // Only include non-"all" values in the filters
    if (data.assetType && data.assetType !== "all") {
      filtersToApply.assetType = data.assetType;
    }

    onApplyFilters(filtersToApply);
  };

  // Handle form reset
  const handleResetFilters = () => {
    form.reset({
      assetType: "all",
    });
    onResetFilters();
  };

  // Custom footer with Reset and Apply buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-4 flex justify-end gap-2 pt-6 border-t border-gray-200">
      <Button type="button" variant="outline" onClick={handleResetFilters}>
        Reset Filters
      </Button>
      <Button type="submit">Apply Filters</Button>
    </div>
  );

  return (
    <DynamicForm
      fields={filterFields}
      form={form}
      onSubmit={handleApplyFilters}
      onReset={handleResetFilters}
      config={{
        layout: "horizontal",
        spacing: "medium",
        showSubmitButton: false,
        showResetButton: false,
        classes: {
          fieldContainer: "flex flex-col gap-1 mb-4",
          label: "text-sm text-gray-800 font-semibold",
          input: "rounded-md border px-3 py-2 border-gray-300 w-full",
          select: "rounded-md border px-3 py-2 border-gray-300",
          textarea: "rounded-md border px-3 py-2 border-gray-300",
          error: "text-sm text-red-500 mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      }}
      className="bg-white p-6 rounded-lg border border-gray-200"
      gridColumns={2}
      responsiveColumns={{
        sm: 2,
        md: 2,
      }}
      renderCustomFooter={renderCustomFooter}
    />
  );
};
